import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  type ReactNode,
} from "react";

// A shell-level "leave this screen" seam. A screen with unsaved edits registers
// one guard; the shell routes its own navigation through guardedLeave so the
// screen can confirm first. Without a provider both sides are no-ops.

/** Returns true when it consumed the leave (a confirm is on screen) and will
 *  call `proceed` itself if the user discards; false when nothing to protect. */
export type LeaveGuard = (proceed?: () => void) => boolean;

type LeaveGuardApi = {
  register: (guard: LeaveGuard) => () => void;
  guardedLeave: (proceed: () => void) => void;
};

const LeaveGuardContext = createContext<LeaveGuardApi | null>(null);

export function LeaveGuardProvider({ children }: { children: ReactNode }) {
  const guardRef = useRef<LeaveGuard | null>(null);
  const register = useCallback((guard: LeaveGuard) => {
    guardRef.current = guard;
    return () => {
      if (guardRef.current === guard) guardRef.current = null;
    };
  }, []);
  const guardedLeave = useCallback((proceed: () => void) => {
    const guard = guardRef.current;
    if (!guard || !guard(proceed)) proceed();
  }, []);
  const api = useMemo(() => ({ register, guardedLeave }), [register, guardedLeave]);
  return <LeaveGuardContext.Provider value={api}>{children}</LeaveGuardContext.Provider>;
}

/** Shell side: `guardedLeave(proceed)`. Runs proceed at once without a guard. */
export function useLeaveGuard(): { guardedLeave: (proceed: () => void) => void } {
  const api = useContext(LeaveGuardContext);
  return { guardedLeave: api ? api.guardedLeave : (proceed) => proceed() };
}

/** Screen side: null without a provider. Returns an unregister function. */
export function useLeaveGuardRegister(): LeaveGuardApi["register"] | null {
  return useContext(LeaveGuardContext)?.register ?? null;
}
